import React, { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router";
import { FaKey, FaSearch, FaTimes, FaCopy, FaCheck } from "react-icons/fa";
import { useTokens } from "@/api/account";
import { Token } from "@/models/account";
import useNotif from "@/hooks/use-notif";

/**
 * TokensSection component - displays API tokens in the sidebar.
 * Shows all tokens with search functionality.
 */
const TokensSection: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: tokens = [] } = useTokens();
  const { addSuccess } = useNotif();

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Filter tokens by search
  const filteredTokens = useMemo(() => {
    if (!searchQuery) return tokens;
    const query = searchQuery.toLowerCase();
    return tokens.filter((token) =>
      token.name?.toLowerCase().includes(query) ?? false
    );
  }, [tokens, searchQuery]);

  // Handle token click - navigate to tokens page
  const handleTokenClick = useCallback(
    (token: Token) => {
      if (!location.pathname.startsWith("/tokens")) {
        navigate("/tokens");
      }
    },
    [navigate, location.pathname]
  );

  // Copy token to clipboard
  const handleCopyToken = useCallback(
    (e: React.MouseEvent, token: Token) => {
      e.stopPropagation();
      navigator.clipboard.writeText(token.token || token.id);
      addSuccess("Token copié");
      setCopiedId(token.id);
      setTimeout(() => setCopiedId(null), 2000);
    },
    [addSuccess]
  );

  // Clear search
  const handleClearSearch = useCallback(() => {
    setSearchQuery("");
    searchInputRef.current?.focus();
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "f" && tokens.length > 0) {
        if (searchInputRef.current) {
          e.preventDefault();
          searchInputRef.current.focus();
        }
      }
      if (e.key === "Escape" && isSearchFocused) {
        setSearchQuery("");
        searchInputRef.current?.blur();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isSearchFocused, tokens.length]);

  // Check if token is expired
  const isExpired = (token: Token) => {
    if (!token.expires_at) return false;
    return new Date(token.expires_at) < new Date();
  };

  if (tokens.length === 0) {
    return (
      <div className="px-3 py-4 text-center text-[var(--arc-sidebar-text-secondary)]">
        <span className="text-xs">Aucun token API</span>
      </div>
    );
  }

  return (
    <div className="px-3 py-2">
      {/* Section Title */}
      <div className="flex items-center gap-2 px-2 mb-2 text-[var(--arc-accent)]">
        <FaKey size={10} />
        <span className="text-[10px] font-semibold uppercase tracking-wider">
          API Tokens
        </span>
        <div className="flex-1 h-px bg-[var(--arc-sidebar-border-accent)]" />
        <span className="text-[10px] text-[var(--arc-sidebar-text-secondary)]">
          {filteredTokens.length}
        </span>
      </div>

      {/* Search Bar */}
      {tokens.length > 3 && (
        <div className="px-1 mb-3">
          <div
            className={`relative flex items-center rounded-md transition-all duration-200 ${
              isSearchFocused
                ? "bg-[var(--arc-sidebar-accent-glow)] ring-1 ring-[var(--arc-accent)]"
                : "bg-[var(--arc-sidebar-accent-glow)] hover:bg-[rgba(var(--arc-accent-rgb),0.15)]"
            }`}
          >
            <FaSearch
              className={`absolute left-2.5 transition-colors duration-200 ${
                isSearchFocused ? "arc-sidebar-icon-active" : ""
              }`}
              size={11}
            />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setIsSearchFocused(false)}
              placeholder="Rechercher..."
              className="w-full bg-transparent text-[var(--arc-sidebar-text-primary)] text-xs py-1.5 pl-7 pr-7 placeholder-[var(--arc-sidebar-text-secondary)] focus:outline-none focus:ring-0"
            />
            {searchQuery && (
              <button
                onClick={handleClearSearch}
                className="absolute right-2 p-0.5 rounded hover:bg-[var(--arc-sidebar-accent-glow)] transition-colors text-[var(--arc-sidebar-text-secondary)] hover:text-[var(--arc-sidebar-text-primary)]"
              >
                <FaTimes size={10} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Tokens List */}
      <div className="space-y-0.5">
        {filteredTokens.map((token) => {
          const expired = isExpired(token);
          
          return (
            <button
              key={token.id}
              onClick={() => handleTokenClick(token)}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left transition-all text-[var(--arc-sidebar-text-secondary)] hover:bg-[var(--arc-sidebar-accent-glow)] hover:text-[var(--arc-sidebar-text-primary)] group"
            >
              <FaKey
                size={10}
                className={expired ? "text-error" : "text-[var(--arc-accent)]"}
              />
              <span className="text-xs truncate flex-1">{token.name}</span>
              {expired && (
                <span className="text-[8px] px-1 py-0.5 bg-error/20 text-error rounded">
                  Expiré
                </span>
              )}
              <button
                onClick={(e) => handleCopyToken(e, token)}
                className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-[var(--arc-sidebar-accent-glow)] transition-all"
              >
                {copiedId === token.id ? (
                  <FaCheck size={10} className="text-success" />
                ) : (
                  <FaCopy size={10} className="text-[var(--arc-sidebar-text-secondary)]" />
                )}
              </button>
            </button>
          );
        })}
      </div>

      {/* No results */}
      {filteredTokens.length === 0 && searchQuery && (
        <div className="px-2 py-3 text-center text-[var(--arc-sidebar-text-secondary)]">
          <span className="text-xs">Aucun résultat</span>
        </div>
      )}
    </div>
  );
};

TokensSection.displayName = "TokensSection";

export default TokensSection;
